var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../sutl';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
const eps = [
    'BFM',
    'BTE_RX',
    'BTE_TX',
    'CTHBFM',
    'CTHBFM_SPKR',
    'FADER',
    'INPUT',
    'MIC',
    'MOBILE_RX',
    'MOBILE_TX',
    'PROC',
    'OUTPUT',
    'SPEAKER',
    'TELCO_RX',
    'UA',
    'USB_RX',
    'USB_TX',
    'VOIP_RX',
];
const audioInputEps = ['BTE_RX', 'FADER', 'INPUT', 'MIC', 'MOBILE_RX', 'PROC', 'USB_RX'];
const audioOutputEps = [
    'BTE_TX',
    'CTHBFM_SPKR',
    'FADER',
    'MOBILE_TX',
    'OUTPUT',
    'PROC',
    'SPEAKER',
    'USB_TX',
];
const micsEps = ['BFM', 'CTHBFM', ...audioInputEps];
function getFilters(ep) {
    return {
        amplifier: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        assistiveListeningMute: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        assistiveListeningMuteStatus: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        assistiveListeningVolume: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        audioInputMute: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        audioInputMuteStatus: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        audioInputVolume: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        mics: audioOutputEps.includes(ep) || micsEps.includes(ep),
        micsMuteStatus: audioOutputEps.includes(ep) || micsEps.includes(ep),
        overflowRoomMute: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        overflowRoomMuteStatus: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        overflowRoomVolume: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        preset: false,
        receiveGain: ['FADER', 'PROC', 'TELCO_RX', 'VOIP_RX'].includes(ep),
        soundReinforcementMute: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        soundReinforcementMuteStatus: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        soundReinforcementVolume: audioOutputEps.includes(ep) || audioInputEps.includes(ep),
        telephony: ['TELCO_RX', 'UA'].includes(ep),
    };
}
export class ConvergePro2 {
    // TODO:
    // - implement voip
    // - emulator
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.detectedChannels = [];
        this.dtmfDurationsMs = {};
        this.gainParameters = {};
        this.muteMacros = {};
        this.networkInitializerSteps = [
            {
                errorCode: 'verifyFailed',
                unterminatedRegExp: /Username:\s*/,
                next: /Username:\s*/,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => {
                    var _a;
                    return (_a = cred.username) !== null && _a !== void 0 ? _a : 'clearone';
                },
                unterminatedRegExp: /.+:\s*/,
                fail: /Username:\s*/,
                next: /Password:\s*/,
            },
            {
                errorCode: 'authFailed',
                send: (cred) => {
                    var _a;
                    return (_a = cred.password) !== null && _a !== void 0 ? _a : 'converge';
                },
                unterminatedRegExp: /(?:.+:|=>)\s*/,
                fail: /Username:\s*/,
                next: /=>\s*/,
            },
            {
                errorCode: 'verifyFailed',
                send: 'STACK SYSTEM SFTYMUTE',
                next: /STACK SYSTEM SFTYMUTE (0|1)/,
            },
        ];
        this.rxGainLabels = {};
        this.serialInitializerSteps = [
            this.networkInitializerSteps[this.networkInitializerSteps.length - 1],
        ];
        this.telephonyTypes = {};
        this.timeouts = new Timeouts();
        this.unmuteMacros = {};
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.detectedChannels;
            }),
            asyncHandlers: [
                {
                    regExp: /^=> BOXMSG \S+ Configuration update completed|^=> STACK NOTIFICATION HW_RESYNC U1 SYSTEM_UP/i,
                    callback: (match) => __awaiter(this, void 0, void 0, function* () {
                        const detectedChannels = yield this.getDetectedChannels(['redetect']);
                        if (!Sutl.objEquals(detectedChannels, this.state.detected.value)) {
                            this.detectedChannels = detectedChannels;
                            this.framework.changePhase('configuring');
                        }
                    }),
                },
            ],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`VERSION * FW 1`, /VERSION \S+ FW 1 (\S+)/, tags);
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const reKey = Sutl.escapeRegExp(key);
                const match = yield this.socket.send(`EP ${key} LEVEL MUTE`, new RegExp(`^=> EP ${reKey} LEVEL MUTE (0|1)|Error Invalid Parameter\\(s\\)`), tags);
                return match[1] ? match[1] === '1' : this.state.mutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const reKey = Sutl.escapeRegExp(key);
                const macro = value ? this.muteMacros[key] : this.unmuteMacros[key];
                if (macro) {
                    const reMacro = Sutl.escapeRegExp(macro);
                    yield this.socket.send(`MCCF ${macro}`, new RegExp(`^=> MCCF ${reMacro}|Error Invalid Parameter\\(s\\)`), tags);
                    const match = yield this.socket.send(`EP ${key} LEVEL MUTE`, new RegExp(`^=> EP ${reKey} LEVEL MUTE (0|1)`), tags);
                    return match[1] === '1';
                }
                else {
                    const match = yield this.socket.send(`EP ${key} LEVEL MUTE ${value ? '1' : '0'}`, new RegExp(`^=> EP ${reKey} LEVEL MUTE (0|1)`), tags);
                    return match[1] === '1';
                }
            }),
            getKey: (config) => {
                return typeof config === 'string' ? config : config.muteStatus;
            },
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (typeof config !== 'string') {
                    this.muteMacros[config.muteStatus] = config.muteMacro;
                    this.unmuteMacros[config.muteStatus] = config.unmuteMacro;
                }
                return this.validateMute(typeof config === 'string' ? config : config.muteStatus);
            }),
            asyncHandlers: [
                {
                    regExp: /^=> EP (\S+) LEVEL MUTE (0|1)/,
                    callback: (match) => {
                        const key = match[1];
                        if (key in this.state.mutes.values) {
                            this.state.mutes.update(key, match[2] === '1');
                        }
                    },
                },
            ],
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                const reKey = Sutl.escapeRegExp(key);
                const match = yield this.socket.send(`EP ${key} KEY KEY_HOOK 1`, new RegExp(`^=> EP ${reKey} KEY KEY_HOOK (\\d+)`));
                return match[1] === '1';
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                for (const [key, type] of Object.entries(this.telephonyTypes)) {
                    const callState = (_a = this.state.calls.values[key]) === null || _a === void 0 ? void 0 : _a.callState;
                    if (value.callType === type &&
                        (callState === 'disconnected' || callState === undefined)) {
                        const reKey = Sutl.escapeRegExp(key);
                        const reDialString = Sutl.escapeRegExp(value.dialString);
                        if (value.callType === 'pstn') {
                            // Provide immediate feedback while we're dialing.
                            this.state.calls.call(key, value.callType, value.callId, value);
                            yield this.socket.send(`EP ${key} KEY KEY_HOOK 1`, new RegExp(`^=> EP ${reKey} KEY KEY_HOOK (\\d+)`));
                            // Send individual digits instead of key_call so we can use the dtmf
                            // duration (if it is defined).
                            yield this.dialDigits(key, value.dialString);
                            // Update the call state to connected.
                            this.state.calls.connect(key);
                            return undefined;
                        }
                        else {
                            // Provide immediate feedback while we're dialing.
                            this.state.calls.call(key, value.callType, value.callId, value);
                            yield this.socket.send(`EP ${key} KEY KEY_CALL ${value.dialString}`, new RegExp(`^=> EP ${reKey} KEY KEY_CALL ${reDialString}`));
                            return undefined;
                        }
                    }
                }
                this.log.error(`call: no available ${value.callType} interface`);
                return undefined;
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                yield this.dialDigits(key, value);
                return value;
            }),
            hangup: (key) => __awaiter(this, void 0, void 0, function* () {
                const reKey = Sutl.escapeRegExp(key);
                const match = yield this.socket.send(`EP ${key} KEY KEY_HOOK 0`, new RegExp(`^=> EP ${reKey} KEY KEY_HOOK (\\d+)`));
                return match[1] === '0';
            }),
            receiveGainRange: { max: 10, min: -10, round: 0.5 },
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                const reKey = Sutl.escapeRegExp(key);
                const type = this.telephonyTypes[key];
                if (type === 'pstn') {
                    yield this.socket.send(`EP ${key} KEY KEY_HOOK 1`, new RegExp(`^=> EP ${reKey} KEY KEY_HOOK 1`));
                    yield this.timeouts.delay(500);
                    const match = yield this.socket.send(`EP ${key} KEY KEY_HOOK 0`, new RegExp(`^=> EP ${reKey} KEY KEY_HOOK (0|1)`));
                    return match[1] === '0';
                }
                else if (type === 'voip') {
                    yield this.socket.send(`EP ${key} KEY KEY_REJECT 1`, new RegExp(`^=> EP ${reKey} KEY KEY_REJECT 1`));
                    return false;
                }
                this.log.error(`invalid key "${key}"`);
                return false;
            }),
            setReceiveGain: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const label = this.rxGainLabels[key];
                if (label === undefined) {
                    this.log.error(`no rxGainLabel for key "${key}"`);
                    return 0;
                }
                return this.setGain(label, value, tags);
            }),
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                const calls = {};
                for (const [key, type] of Object.entries(this.telephonyTypes)) {
                    const reKey = Sutl.escapeRegExp(key);
                    let rxGain;
                    if (this.rxGainLabels[key]) {
                        rxGain = yield this.getGain(this.rxGainLabels[key], []);
                    }
                    if (type === 'pstn') {
                        const hookMatch = yield this.socket.send(`EP ${key} INQUIRE HOOK`, new RegExp(`^=> EP ${reKey} INQUIRE HOOK (\\d+)`));
                        const call = Object.assign({ callType: 'pstn', canDial: true }, (rxGain !== undefined ? { receiveGain: rxGain } : undefined));
                        if (hookMatch[1] === '1') {
                            const callerIdMatch = yield this.socket.send(`EP ${key} INQUIRE CALLER_ID`, new RegExp(`^=> EP ${reKey} INQUIRE CALLER_ID (\\S+)`));
                            const directionMatch = yield this.socket.send(`EP ${key} INQUIRE DIRECTION`, new RegExp(`^=> EP ${reKey} INQUIRE DIRECTION (\\S+)`));
                            const durationMatch = yield this.socket.send(`EP ${key} INQUIRE OFF_HOOK_DURATION`, new RegExp(`^=> EP ${reKey} INQUIRE OFF_HOOK_DURATION (\\d+)`));
                            call.callState = 'connected';
                            if (callerIdMatch[1] !== 'NO_ACTIVE_CALL') {
                                call.dialString = callerIdMatch[1];
                            }
                            call.direction = directionMatch[1] === '1' ? 'incoming' : 'outgoing';
                            const durationSec = parseInt(durationMatch[1], 10);
                            if (durationSec > 0)
                                call.start = Date.now() - durationSec * 1000;
                        }
                        else {
                            call.callState = 'disconnected';
                        }
                        calls[key] = this.state.calls.addMissing(call);
                    }
                    else {
                        const activeMatch = yield this.socket.send(`EP ${key} INQUIRE ACTIVE_PARTIES`, new RegExp(`^=> EP ${reKey} INQUIRE_RESULT ACTIVE_PARTIES ([^:;]+)(?::(.+):(\\\d+);)?`));
                        const [, state, callerId, duration] = activeMatch;
                        const call = Object.assign({ callType: 'voip', canDial: true }, (rxGain !== undefined ? { receiveGain: rxGain } : undefined));
                        if (state === 'IDLE') {
                            call.callState = 'disconnected';
                        }
                        else if (state === 'DIALING' || state === 'INPROCESS') {
                            call.callState = 'ringing';
                            call.direction = 'outgoing';
                        }
                        else if (state === 'ACTIVE' ||
                            state === 'BLIND_TRANSFER_HOLD' ||
                            state === 'BLIND_TRANSFERRING_DIAL_TONE' ||
                            state === 'BLIND_TRANSFERRING_DIALING' ||
                            state === 'BLIND_TRANSFERRING_INPROCESS' ||
                            state === 'BLIND_TRANSFERRING_RINGING' ||
                            state === 'BLIND_TRANSFERRING_BUSY' ||
                            state === 'BUSY' ||
                            state === 'CONFERENCE_HOLD' ||
                            state === 'HOLD' ||
                            state === 'TRANSFER_ACTIVE' ||
                            state === 'TRANSFER_HOLD' ||
                            state === 'TRANSFERRING_DIAL_TONE' ||
                            state === 'TRANSFERRING_DIALING') {
                            call.callState = 'connected';
                            call.direction = 'outgoing';
                            this.addCallerId(call, callerId);
                            if (duration) {
                                const durationSec = parseInt(duration, 10);
                                if (durationSec > 0)
                                    call.start = Date.now() - durationSec * 1000;
                            }
                            if (state === 'BLIND_TRANSFER_HOLD' ||
                                state === 'CONFERENCE_HOLD' ||
                                state === 'HOLD' ||
                                state === 'TRANSFER_HOLD') {
                                call.callExtendedState = 'hold';
                            }
                        }
                        else if (state === 'RINGING') {
                            call.callState = 'ringing';
                            call.direction = 'outgoing';
                            this.addCallerId(call, callerId);
                        }
                        else if (state === 'INCOMING') {
                            call.callState = 'ringing';
                            call.direction = 'incoming';
                            this.addCallerId(call, callerId);
                        }
                        else {
                            this.log.error(`unhandled voip call state: ${activeMatch[1]}`);
                            call.callState = 'disconnected';
                        }
                        calls[key] = this.state.calls.addMissing(call);
                    }
                }
                return calls;
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const maxCalls = {};
                for (const type of Object.values(this.telephonyTypes)) {
                    maxCalls[type] = ((_b = maxCalls[type]) !== null && _b !== void 0 ? _b : 0) + 1;
                }
                return maxCalls;
            }),
            getKey: (config) => config.label,
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const reLabel = Sutl.escapeRegExp(config.label);
                const result = { label: 'missing' };
                const callerIdMatch = yield this.socket.send(`EP ${config.label} INQUIRE CALLER_ID`, new RegExp(`^=> EP ${reLabel} INQUIRE CALLER_ID (\\S+)|^=> Error`), ['validate']);
                if (callerIdMatch[1] !== undefined) {
                    this.telephonyTypes[config.label] = 'pstn';
                    result.label = 'ok';
                }
                else {
                    const regStatusMatch = yield this.socket.send(`EP ${config.label} INQUIRE REG_STATUS`, new RegExp(`^=> EP ${reLabel} INQUIRE_RESULT REG_STATUS (\\S+)|^=> Error`), ['validate']);
                    if (regStatusMatch[1] !== undefined) {
                        this.telephonyTypes[config.label] = 'voip';
                        result.label = 'ok';
                    }
                }
                if (config.rxGainLabel) {
                    this.rxGainLabels[config.label] = config.rxGainLabel;
                    result.rxGainLabel = yield this.validateGain(config.rxGainLabel);
                }
                return result;
            }),
            asyncHandlers: [
                {
                    regExp: /^=> EP (\S+) NOTIFICATION CALLER_ID (\S+)/,
                    callback: (match) => {
                        const key = match[1];
                        if (this.telephonyTypes[key] !== 'pstn')
                            return;
                        const call = this.state.calls.values[key];
                        if ((call === null || call === void 0 ? void 0 : call.callState) === 'connected' && (call === null || call === void 0 ? void 0 : call.direction) === 'incoming') {
                            this.state.calls.updateCall(key, { dialString: match[2] });
                        }
                    },
                },
                {
                    regExp: /^=> EP (\S+) (?:NOTIFICATION HOOK|KEY KEY_HOOK) (\d+)/,
                    callback: (match) => {
                        const key = match[1];
                        if (match[2] === '0') {
                            this.state.calls.disconnect(key);
                        }
                        else if (match[2] === '1') {
                            this.state.calls.connect(key);
                        }
                    },
                },
                {
                    regExp: /^=> EP (\S+) NOTIFICATION INCOMING_CALL (\d+)/,
                    callback: (match) => {
                        const key = match[1];
                        if (this.telephonyTypes[key] !== 'pstn')
                            return;
                        const call = this.state.calls.values[key];
                        if (match[2] === '1') {
                            this.state.calls.setIncoming(key);
                        }
                        else {
                            if ((call === null || call === void 0 ? void 0 : call.callState) === 'ringing') {
                                this.state.calls.disconnect(key);
                            }
                        }
                    },
                },
                {
                    regExp: /^=> EP (\S+) NOTIFICATION STATE_CHANGE PL 1;([^: ]+)[ ]*(?::(.+\S))?/,
                    callback: (match) => {
                        const [, key, state, callerId] = match;
                        if (this.telephonyTypes[key] !== 'voip')
                            return;
                        if (state === 'IDLE') {
                            this.state.calls.disconnect(key, { callExtendedState: null });
                        }
                        else if (state === 'ACTIVE' || state === 'TRANSFER_ACTIVE') {
                            let extras;
                            if (callerId !== undefined) {
                                extras = {};
                                this.addCallerId(extras, callerId);
                            }
                            this.state.calls.connect(key, extras);
                        }
                        else if (state === 'INCOMING') {
                            let extras;
                            if (callerId !== undefined) {
                                extras = {};
                                this.addCallerId(extras, callerId);
                            }
                            this.state.calls.setIncoming(key, 'voip', extras);
                        }
                        else if (state === 'DIAL_TONE' ||
                            state === 'INPROCESS' ||
                            state === 'RINGING') {
                            let extras;
                            if (callerId !== undefined) {
                                extras = {};
                                this.addCallerId(extras, callerId);
                            }
                            this.state.calls.setOutgoing(key, 'voip', extras);
                        }
                        else if (state === 'BLIND_TRANSFER_HOLD' ||
                            state === 'CONFERENCE_HOLD' ||
                            state === 'HOLD' ||
                            state === 'TRANSFER_HOLD') {
                            this.state.calls.updateCallExtendedState(key, 'hold');
                        }
                    },
                },
            ],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getGain(key, tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.setGain(key, value, tags);
            }),
            getKey: (config) => config,
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 12, min: -12, round: 0.5 };
                return { max: 20, min: -30, round: 0.5 };
            }),
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                return this.validateGain(config);
            }),
            asyncHandlers: [
                {
                    regExp: /^=> EP (\S+) LEVEL (GAIN|GAIN_FINE) (\S+)/,
                    callback: (match) => {
                        const key = match[1];
                        if (key in this.state.volumes.values) {
                            this.state.volumes.update(key, parseInt(match[3], 10));
                        }
                        else if (key in this.telephonyTypes) {
                            this.state.calls.updateReceiveGain(key, parseInt(match[3], 10));
                        }
                    },
                },
            ],
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const tags = ['poll'];
                    yield this.socket.send(`STACK SYSTEM SFTYMUTE`, /^=> STACK SYSTEM SFTYMUTE (0|1)/, tags);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.dtmfDurationsMs = {};
            for (const [key, tc] of Object.entries((_a = config.telephony) !== null && _a !== void 0 ? _a : {})) {
                if (typeof tc === 'object' && 'dtmfDurationMs' in tc && tc.dtmfDurationMs) {
                    this.dtmfDurationsMs[key] = tc.dtmfDurationMs;
                }
            }
            this.gainParameters = {};
            this.muteMacros = {};
            this.rxGainLabels = {};
            this.telephonyTypes = {};
            this.unmuteMacros = {};
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield CommandSocketInitializer.initialize(this.socket, this.socket.port === 23 ? this.networkInitializerSteps : this.serialInitializerSteps, credentials);
            if (result === 'connected') {
                this.detectedChannels = yield this.getDetectedChannels(['initialize', 'get.detected']);
            }
            return result;
        });
    }
    // other class methods
    addCallerId(call, value) {
        if (value === undefined)
            return;
        const match = /^"(.+)"\s*<(.*)>/.exec(value);
        if (call.dialString === undefined && match) {
            call.dialString = match[2];
        }
        call.contactName = match ? match[1] : value;
    }
    dialDigits(key, digits) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const type = this.telephonyTypes[key];
            if (type === undefined) {
                this.log.error(`invalid key "${key}"`);
                return;
            }
            const reKey = Sutl.escapeRegExp(key);
            for (const ch of digits) {
                const reCh = Sutl.escapeRegExp(ch);
                yield this.socket.send(`EP ${key} KEY KEY_DIGIT_PRESSED ${ch}`, new RegExp(`^=> EP ${reKey} KEY KEY_DIGIT_PRESSED ${reCh}`));
                yield this.timeouts.delay((_a = this.dtmfDurationsMs[key]) !== null && _a !== void 0 ? _a : 250);
                yield this.socket.send(`EP ${key} KEY KEY_DIGIT_RELEASED ${ch}`, new RegExp(`^=> EP ${reKey} KEY KEY_DIGIT_RELEASED ${reCh}`));
            }
        });
    }
    getDetectedChannels(tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const devices = [];
                const detectTimeout = this.timeouts.create();
                const locate = tags.includes('initialize');
                if (locate) {
                    this.socket.sendAsync(`BOX * LOCATE ENABLE 0`, tags);
                    this.socket.sendAsync(`BOX * LOCATE ENABLE 1`, tags);
                }
                const id = this.socket.registerAsync(/^=> BOX (\S+) DID (\d+)/, (match) => {
                    devices.push({ id: parseInt(match[2], 10), name: match[1] });
                    detectTimeout.restart(2000);
                });
                detectTimeout.set(() => __awaiter(this, void 0, void 0, function* () {
                    this.socket.unregisterAsync(id);
                    const channels = [];
                    for (const device of devices) {
                        for (const ep of eps) {
                            const reEp = Sutl.escapeRegExp(ep);
                            for (let i = 1; i < 33; i++) {
                                const epn = `${device.id}${i.toString().padStart(2, '0')}`;
                                const reEpn = Sutl.escapeRegExp(epn);
                                try {
                                    const match = yield this.socket.send(`EP ${ep} ${epn} LABEL`, new RegExp(`^=> EP ${reEp} (?:(\\d+) )?${reEpn} LABEL(?: (\\S+))?|Error Invalid Parameter\\(s\\)`), tags);
                                    if (match[2] !== undefined) {
                                        if (match[2].startsWith('Error') ||
                                            match[2].startsWith('Invalid')) {
                                            break;
                                        }
                                        channels.push({
                                            label: match[2],
                                            type: ep,
                                            filters: getFilters(ep),
                                        });
                                    }
                                }
                                catch (error) {
                                    this.log.error(error.message);
                                }
                            }
                        }
                    }
                    if (locate) {
                        this.socket.sendAsync(`BOX * LOCATE ENABLE 0`, tags);
                    }
                    resolve(channels);
                }), 2000);
                try {
                    this.socket.sendAsync('BOX * DID', tags);
                }
                catch (error) {
                    detectTimeout.clear();
                    this.socket.unregisterAsync(id);
                }
            });
        });
    }
    getGain(label, tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const reLabel = Sutl.escapeRegExp(label);
            const param = (_a = this.gainParameters[label]) !== null && _a !== void 0 ? _a : 'GAIN';
            const match = yield this.socket.send(`EP ${label} LEVEL ${param}`, new RegExp(`^=> EP ${reLabel} LEVEL ${param} (\\S+)`), tags);
            return parseInt(match[1], 10);
        });
    }
    setGain(label, value, tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const reLabel = Sutl.escapeRegExp(label);
            const param = (_a = this.gainParameters[label]) !== null && _a !== void 0 ? _a : 'GAIN';
            const match = yield this.socket.send(`EP ${label} LEVEL ${param} ${value.toFixed(1)}`, new RegExp(`^=> EP ${reLabel} LEVEL ${param} (\\S+)`), tags);
            return parseInt(match[1], 10);
        });
    }
    validateGain(label) {
        return __awaiter(this, void 0, void 0, function* () {
            const reLabel = Sutl.escapeRegExp(label);
            const fineGainMatch = yield this.socket.send(`EP ${label} LEVEL GAIN_FINE`, new RegExp(`^=> EP ${reLabel} LEVEL GAIN_FINE (\\S+)|Error Invalid Parameter\\(s\\)`), ['validate']);
            if (fineGainMatch[1] !== undefined) {
                this.gainParameters[label] = 'GAIN_FINE';
                return 'ok';
            }
            const gainMatch = yield this.socket.send(`EP ${label} LEVEL GAIN`, new RegExp(`^=> EP ${reLabel} LEVEL GAIN (\\S+)|Error Invalid Parameter\\(s\\)`), ['validate']);
            if (gainMatch[1] === undefined)
                return 'missing';
            this.gainParameters[label] = 'GAIN';
            return 'ok';
        });
    }
    validateMute(label) {
        return __awaiter(this, void 0, void 0, function* () {
            const reLabel = Sutl.escapeRegExp(label);
            const match = yield this.socket.send(`EP ${label} LEVEL MUTE`, new RegExp(`^=> EP ${reLabel} LEVEL MUTE (0|1)|Error Invalid Parameter\\(s\\)`), ['validate']);
            return match[1] === undefined ? 'missing' : 'ok';
        });
    }
}
// static properties
ConvergePro2.channelsConfigName = 'convergePro2ControlChannels';
ConvergePro2.driverType = 'socketDriver';
ConvergePro2.key = '-L6vr3WoJJpmHv3vubko';
ConvergePro2.socketOptions = [{ port: 23 }, { port: 4999 }];
